// ---------------------------------------------------------------------------
// Material definition parsing and serialization
// ---------------------------------------------------------------------------

/**
 * Parse a material_def(...) function call into a 22-element array:
 * [metalness, roughness, emissive_r, emissive_g, emissive_b, emissive_intensity,
 *  clearcoat, clearcoat_roughness,
 *  sheen, sheen_roughness, sheen_color_r, sheen_color_g, sheen_color_b,
 *  iridescence, iridescence_ior,
 *  transmission, ior, thickness,
 *  attenuation_color_r, attenuation_color_g, attenuation_color_b, attenuation_distance]
 *
 * Handles named arguments like:
 *   material_def(metalness=0.9, roughness=0.4)
 *   material_def(roughness=0.8, emissive=[1,0,0], emissive_intensity=3.0)
 *   material_def(clearcoat=1, transmission=0.9, ior=1.5)
 *
 * Default values: -1 for each property (meaning "use global default")
 */
export function parseMaterialDef(valueStr: string): number[] {
  // 22-element array: all default to -1
  const result = Array(22).fill(-1);

  // Extract the arguments string from material_def(...)
  const argsMatch = valueStr.match(/^material_def\((.+)\)$/s);
  if (!argsMatch) return result;

  const argsStr = argsMatch[1];

  // Parse named arguments, handling nested brackets for array values
  // Split on commas that are NOT inside brackets
  const args: string[] = [];
  let depth = 0;
  let current = '';
  for (const ch of argsStr) {
    if (ch === '[') depth++;
    else if (ch === ']') depth--;
    else if (ch === ',' && depth === 0) {
      args.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  if (current.trim()) args.push(current.trim());

  const parseVec3 = (val: string): [number, number, number] | null => {
    const m = val.match(
      /\[\s*(-?\d+(\.\d+)?)\s*,\s*(-?\d+(\.\d+)?)\s*,\s*(-?\d+(\.\d+)?)\s*\]/,
    );
    if (m) return [parseFloat(m[1]), parseFloat(m[3]), parseFloat(m[5])];
    return null;
  };

  for (const arg of args) {
    const eqIdx = arg.indexOf('=');
    if (eqIdx === -1) continue;
    const key = arg.slice(0, eqIdx).trim();
    const val = arg.slice(eqIdx + 1).trim();

    switch (key) {
      case 'metalness':
        result[0] = parseFloat(val);
        break;
      case 'roughness':
        result[1] = parseFloat(val);
        break;
      case 'emissive': {
        const v = parseVec3(val);
        if (v) {
          result[2] = v[0];
          result[3] = v[1];
          result[4] = v[2];
        }
        break;
      }
      case 'emissive_intensity':
        result[5] = parseFloat(val);
        break;
      case 'clearcoat':
        result[6] = parseFloat(val);
        break;
      case 'clearcoat_roughness':
        result[7] = parseFloat(val);
        break;
      case 'sheen':
        result[8] = parseFloat(val);
        break;
      case 'sheen_roughness':
        result[9] = parseFloat(val);
        break;
      case 'sheen_color': {
        const v = parseVec3(val);
        if (v) {
          result[10] = v[0];
          result[11] = v[1];
          result[12] = v[2];
        }
        break;
      }
      case 'iridescence':
        result[13] = parseFloat(val);
        break;
      case 'iridescence_ior':
        result[14] = parseFloat(val);
        break;
      case 'transmission':
        result[15] = parseFloat(val);
        break;
      case 'ior':
        result[16] = parseFloat(val);
        break;
      case 'thickness':
        result[17] = parseFloat(val);
        break;
      case 'attenuation_color': {
        const v = parseVec3(val);
        if (v) {
          result[18] = v[0];
          result[19] = v[1];
          result[20] = v[2];
        }
        break;
      }
      case 'attenuation_distance':
        result[21] = parseFloat(val);
        break;
    }
  }

  return result;
}

/**
 * Serialize a 22-element material array back into a material_def() function call string.
 * Only includes properties that are not -1 (default).
 *
 * [metalness, roughness, emissive_r, emissive_g, emissive_b, emissive_intensity,
 *  clearcoat, clearcoat_roughness,
 *  sheen, sheen_roughness, sheen_color_r, sheen_color_g, sheen_color_b,
 *  iridescence, iridescence_ior,
 *  transmission, ior, thickness,
 *  attenuation_color_r, attenuation_color_g, attenuation_color_b, attenuation_distance]
 */
export function serializeMaterialDef(values: number[]): string {
  const parts: string[] = [];
  const fmt = (n: number) => {
    // Format: remove trailing zeros but keep at least one decimal
    const s = n.toFixed(4);
    return parseFloat(s).toString();
  };

  const fmtVec3 = (a: number, b: number, c: number): string => {
    const va = a >= 0 ? fmt(a) : '0';
    const vb = b >= 0 ? fmt(b) : '0';
    const vc = c >= 0 ? fmt(c) : '0';
    return `[${va},${vb},${vc}]`;
  };

  if (values[0] >= 0) parts.push(`metalness=${fmt(values[0])}`);
  if (values[1] >= 0) parts.push(`roughness=${fmt(values[1])}`);
  if (values[2] >= 0 || values[3] >= 0 || values[4] >= 0) {
    parts.push(`emissive=${fmtVec3(values[2], values[3], values[4])}`);
  }
  if (values[5] >= 0) parts.push(`emissive_intensity=${fmt(values[5])}`);
  if (values[6] >= 0) parts.push(`clearcoat=${fmt(values[6])}`);
  if (values[7] >= 0) parts.push(`clearcoat_roughness=${fmt(values[7])}`);
  if (values[8] >= 0) parts.push(`sheen=${fmt(values[8])}`);
  if (values[9] >= 0) parts.push(`sheen_roughness=${fmt(values[9])}`);
  if (values[10] >= 0 || values[11] >= 0 || values[12] >= 0) {
    parts.push(`sheen_color=${fmtVec3(values[10], values[11], values[12])}`);
  }
  if (values[13] >= 0) parts.push(`iridescence=${fmt(values[13])}`);
  if (values[14] >= 0) parts.push(`iridescence_ior=${fmt(values[14])}`);
  if (values[15] >= 0) parts.push(`transmission=${fmt(values[15])}`);
  if (values[16] >= 0) parts.push(`ior=${fmt(values[16])}`);
  if (values[17] >= 0) parts.push(`thickness=${fmt(values[17])}`);
  if (values[18] >= 0 || values[19] >= 0 || values[20] >= 0) {
    parts.push(
      `attenuation_color=${fmtVec3(values[18], values[19], values[20])}`,
    );
  }
  if (values[21] >= 0) parts.push(`attenuation_distance=${fmt(values[21])}`);

  return `material_def(${parts.join(', ')})`;
}
